/**
 * Form.js — Async form submit with fetch, loading state, disabled, error handling.
 * Usage: new Form(formElement, { submitUrl, onSuccess, onError, ... }).init();
 */
export class Form {
    /**
     * @param {HTMLFormElement} form
     * @param {Object} options
     * @param {string} options.submitUrl - API URL for POST
     * @param {string} [options.method='POST']
     * @param {Function} [options.onSuccess] - (data) => void
     * @param {Function} [options.onError] - (error, message?) => void
     * @param {string} [options.loadingText] - Button text while submitting
     * @param {string} [options.submitButtonSelector] - Selector for submit button (default: [type="submit"])
     */
    constructor(form, options = {}) {
        this.form = form;
        this.submitUrl = options.submitUrl || (form && form.getAttribute("data-submit-url"));
        this.method = (options.method || "POST").toUpperCase();
        this.onSuccess = options.onSuccess || (() => {});
        this.onError = options.onError || (() => {});
        this.loadingText = options.loadingText ?? null;
        this.submitButtonSelector = options.submitButtonSelector || '[type="submit"]';
        this.submitBtn = null;
        this.originalBtnHtml = null;
        this.isSubmitting = false;
    }

    init() {
        if (!this.form || !this.submitUrl) return;

        this.submitBtn = this.form.querySelector(this.submitButtonSelector);
        if (this.submitBtn) {
            this.originalBtnHtml = this.submitBtn.innerHTML;
        }

        this.form.addEventListener("submit", (e) => this.handleSubmit(e));

        // Contact form: captcha refresh button
        if (this.form.hasAttribute("data-contact-form")) {
            this.bindCaptchaRefresh();
        }
    }

    bindCaptchaRefresh() {
        const refreshBtn = document.getElementById("contact-captcha-refresh");
        const img = document.getElementById("contact-captcha-img");
        if (!refreshBtn || !img) return;

        const getCaptchaUrl = () => {
            try {
                const ctx = window.Context && window.Context.getInstance();
                if (ctx && ctx.apiUrls && ctx.apiUrls["captcha.image"]) {
                    return ctx.apiUrls["captcha.image"] + "?t=" + Date.now();
                }
            } catch (_) {}
            return "/api/captcha/image?t=" + Date.now();
        };

        refreshBtn.addEventListener("click", () => {
            img.src = getCaptchaUrl();
            const input = document.getElementById("contact-captcha");
            if (input) input.value = "";
        });
    }

    async handleSubmit(e) {
        e.preventDefault();
        if (this.isSubmitting) return;

        const form = this.form;
        const url = this.submitUrl;
        const submitBtn = this.submitBtn;

        this.setSubmitting(true, submitBtn);

        try {
            const body = new FormData(form);
            const response = await fetch(url, {
                method: this.method,
                body,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            const data = await response.json().catch(() => ({}));
            const message = data.message || data.msg || (data.error ? String(data.error) : null);

            if (!response.ok) {
                throw new Error(message || `Request failed (${response.status})`);
            }

            if (data.success === false && message) {
                throw new Error(message);
            }

            this.onSuccess(data);
        } catch (err) {
            const msg = err && err.message ? err.message : "An error occurred. Please try again.";
            this.onError(err, msg);
        } finally {
            this.setSubmitting(false, submitBtn);
        }
    }

    setSubmitting(submitting, submitBtn) {
        this.isSubmitting = submitting;

        if (!submitBtn) return;

        submitBtn.disabled = submitting;
        submitBtn.setAttribute("aria-busy", submitting ? "true" : "false");

        if (submitting) {
            const textSpan = submitBtn.querySelector("span");
            const icon = submitBtn.querySelector("i");
            if (textSpan) textSpan.textContent = this.loadingText || "Sending...";
            if (icon) {
                icon.classList.remove("fa-arrow-right", "fa-light");
                icon.classList.add("fa-solid", "fa-spinner", "fa-spin");
            }
        } else {
            if (this.originalBtnHtml) submitBtn.innerHTML = this.originalBtnHtml;
        }
    }
}

/**
 * Initialize all forms with [data-contact-form] and data-submit-url.
 */
export function initContactForms() {
    const forms = document.querySelectorAll("form[data-contact-form][data-submit-url]");
    forms.forEach((form) => {
        const url = form.getAttribute("data-submit-url");
        if (!url) return;
        const formHandler = new Form(form, {
            submitUrl: url,
            loadingText: null, // use translation or default "Sending..."
        });
        formHandler.onSuccess = (data) => {
            const msg = data.message || data.msg || "Thank you. Your message has been sent.";
            if (typeof alert === "function") alert(msg);
            form.reset();
            const img = document.getElementById("contact-captcha-img");
            if (img) {
                try {
                    const ctx = window.Context && window.Context.getInstance();
                    const url = (ctx && ctx.apiUrls && ctx.apiUrls["captcha.image"])
                        ? ctx.apiUrls["captcha.image"] + "?t=" + Date.now()
                        : "/api/captcha/image?t=" + Date.now();
                    img.src = url;
                } catch (_) {
                    img.src = "/api/captcha/image?t=" + Date.now();
                }
            }
        };
        formHandler.onError = (_err, message) => {
            if (typeof alert === "function") alert(message || "Something went wrong. Please try again.");
        };
        formHandler.init();
    });
}
